import { motion } from "framer-motion";
import { useAudio } from "@/components/audio-provider";
import { cn } from "@/lib/utils";
import { SpeedControl } from "@/components/speed-control";

/**
 * Playback controls — opened from the header speaker button. Renders as a
 * bottom sheet on mobile and a top-right popover on desktop, so it never
 * takes persistent space in the header or grid. Holds the speed/pitch lab
 * (presets + fine slider) and the mono/poly toggle.
 */
export function PlaybackPanel({ onClose }: { onClose: () => void }) {
  const { polyphonic, setPolyphonic } = useAudio();

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        role="dialog"
        aria-label="Playback settings"
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-mist bg-slate p-4 pb-7 shadow-2xl sm:inset-x-auto sm:bottom-auto sm:right-3 sm:top-14 sm:w-72 sm:rounded-2xl sm:border sm:pb-4"
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-mist sm:hidden" />

        <SpeedControl />

        <div className="my-4 h-px bg-mist" />

        {/* Mono / Poly */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-parchment">Layer sounds</div>
            <div className="text-[11px] text-fog">
              {polyphonic ? "Play several at once" : "One sound at a time"}
            </div>
          </div>
          <button
            onClick={() => setPolyphonic(!polyphonic)}
            role="switch"
            aria-checked={polyphonic}
            aria-label="Layer sounds"
            className={cn(
              "relative h-6 w-11 shrink-0 rounded-full transition-colors",
              polyphonic ? "bg-spark" : "bg-mist",
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                polyphonic ? "left-[22px]" : "left-0.5",
              )}
            />
          </button>
        </div>
      </motion.div>
    </>
  );
}
